import CustomCollapsible from "@/components/ui/collapsibleCard/collapsible-card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormWatch,
} from "react-hook-form";
import { Product } from "@/types/system-setting/product";
import {
  FileUploader,
  FileUploaderContent,
  FileUploaderItem,
  FileInput,
} from "@/components/ui/file-uploader";
import { cn } from "@/lib/utils";
import { Spinner } from "@/components/ui/spinner/spinner";

interface ProductSectionProp {
  control: Control<Product>;
  errors: FieldErrors<Product>;
  watch: UseFormWatch<Product>;
  selectedImageFiles: File[] | null;
  previewUrls: string[];
  isUploadingImage: boolean;
  onFileUpload: (files: File[] | null) => void;
  onRemoveImage: () => void;
  VITE_VIEW_S3_URL: string;
}

const ProductSection = ({
  control,
  errors,
  watch,
  selectedImageFiles,
  previewUrls,
  isUploadingImage,
  onFileUpload,
  onRemoveImage,
  VITE_VIEW_S3_URL,
}: ProductSectionProp) => {
  const watcher = watch();
  
  return (
    <div className="w-[95%] rounded-2xl bg-white">
      <CustomCollapsible
        title="Product Details"
        hideToggle={false}
        isOpenDefault={true}
      >
        <div className="grid grid-cols-12 gap-4">
          {/* Image Upload Section - Left Side */}
          <div className="col-span-12 md:col-span-4">
            <p className="text-sm font-medium mb-2">
              Image <span className="text-red-500">*</span>
            </p>
            <FileUploader
              value={selectedImageFiles}
              onValueChange={onFileUpload}
              dropzoneOptions={{
                accept: {
                  "image/png": [".png"],
                  "image/jpeg": [".jpg", ".jpeg"],
                  "image/jpg": [".jpg"],
                },
                multiple: false,
                maxFiles: 1,
                maxSize: 1 * 1024 * 1024,
                disabled: isUploadingImage,
              }}
              orientation="vertical"
            >
              {(!selectedImageFiles || selectedImageFiles.length === 0) &&
                !watcher.image_url && (
                  <FileInput>
                    <div
                      className={cn(
                        "flex items-center justify-center h-40 w-full border-2 border-dashed bg-background rounded-lg transition-colors",
                        isUploadingImage && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      {isUploadingImage ? (
                        <div className="flex flex-col items-center gap-2">
                          <Spinner size="small" />
                          <p className="text-xs text-gray-400">Uploading...</p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-1.5 text-center px-3">
                          <i className="fa fa-cloud-upload text-2xl text-gray-400"></i>
                          <p className="text-xs text-gray-600">
                            Drop or click to upload
                          </p>
                          <p className="text-[10px] text-gray-400">
                            PNG, JPG (Max 1MB)
                          </p>
                        </div>
                      )}
                    </div>
                  </FileInput>
                )}

              <FileUploaderContent className="mt-2">
                {selectedImageFiles && selectedImageFiles.length > 0 && previewUrls.length > 0 ? (
                  selectedImageFiles.map((file, index) => (
                    <FileUploaderItem
                      key={index}
                      index={index}
                      onRemove={onRemoveImage}
                      className="h-40 p-0 rounded-lg overflow-hidden relative border"
                      aria-roledescription="uploaded product image"
                    >
                      <img
                        src={previewUrls[index]}
                        alt="Product preview"
                        className="w-full h-full object-cover"
                      />
                    </FileUploaderItem>
                  ))
                ) : watcher.image_url ? (
                  <FileUploaderItem
                    index={0}
                    onRemove={onRemoveImage}
                    className="h-40 p-0 rounded-lg overflow-hidden relative border"
                    aria-roledescription="existing product image"
                  >
                    <img
                      src={
                        watcher.image_url.includes("https://")
                          ? watcher.image_url
                          : `${VITE_VIEW_S3_URL}/${watcher.image_url}`
                      }
                      alt="Product image"
                      className="w-full h-full object-cover"
                    />
                  </FileUploaderItem>
                ) : null}
              </FileUploaderContent>
            </FileUploader>
          </div>

          {/* Product Details - Right Side */}
          <div className="col-span-12 md:col-span-8 space-y-4">
            {/* First Row: Name, SKU, Vendor */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm font-medium mb-1.5">
                  Name <span className="text-red-500">*</span>
                </p>
                <Controller
                  control={control}
                  name="product_name"
                  rules={{
                    required: "Required",
                    maxLength: {
                      value: 40,
                      message: "Name cannot exceed 40 characters",
                    },
                    validate: (value) =>
                      value.trim() !== "" || "Name cannot be empty or only spaces",
                  }}
                  render={({ field }) => (
                    <Input className="w-full h-9" placeholder="Enter Name" {...field} />
                  )}
                />
                {errors.product_name?.message && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.product_name.message.toString()}
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm font-medium mb-1.5">
                  Product SKU <span className="text-red-500">*</span>
                </p>
                <Controller
                  control={control}
                  name="sku"
                  rules={{
                    required: "Required",
                    maxLength: {
                      value: 10,
                      message: "SKU cannot exceed 10 characters",
                    },
                    validate: (value) =>
                      value.trim() !== "" || "SKU cannot be empty or only spaces",
                  }}
                  render={({ field }) => (
                    <Input
                      className="w-full h-9"
                      placeholder="Enter Product SKU"
                      {...field}
                    />
                  )}
                />
                {errors.sku?.message && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.sku.message.toString()}
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm font-medium mb-1.5">Vendor Name</p>
                <Controller
                  control={control}
                  name="vendor"
                  rules={{
                    maxLength: {
                      value: 40,
                      message: "Vendor Name cannot exceed 40 characters",
                    }
                  }}
                  render={({ field }) => (
                    <Input
                      className="w-full h-9"
                      placeholder="Enter Vendor Name"
                      {...field}
                    />
                  )}
                />
                {errors.vendor?.message && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.vendor.message.toString()}
                  </p>
                )}
              </div>
            </div>

            {/* Description */}
            <div>
              <p className="text-sm font-medium mb-1.5">Description</p>
              <Controller
                control={control}
                name="description"
                rules={{
                  maxLength: {
                    value: 200,
                    message: "Description cannot exceed 200 characters",
                  },
                }}
                render={({ field }) => (
                  <Textarea
                    placeholder="Type your Description here"
                    className="resize-none h-20"
                    {...field}
                    value={field.value ?? ""}
                  />
                )}
              />
              {errors.description?.message && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.description.message.toString()}
                </p>
              )}
            </div>
          </div>
        </div>
      </CustomCollapsible>
    </div>
  );
};

export default ProductSection;
